import React, { useState, useEffect, useCallback, useReducer } from 'react';
import './BackendDemo.css';

const NODE_DETAILS = {
    gateway: {
        title: 'Vert.x Edge Gateway',
        icon: '📡',
        tech: 'Vert.x + Project Loom',
        role: 'Non-blocking reactive gateway utilizing a multi-reactor pattern. Orchestrates high-frequency routing and security with zero-copy buffer handling.',
        ai: [
            '🤖 AI Insight: Vert.x acts as the extremely fast "Front Door" of this live EC2 backend. Real traffic is actively flowing through this non-blocking gateway, ensuring the server doesn\'t crash under heavy load.',
            '💡 Deeper Look: Traditional web servers spawn a costly thread per user. Vert.x uses an "Event Loop"—handling 10,000+ connections per core. It is the perfect choice to prevent "Black Friday" retail crashes.',
            '⚙️ Architecture Note: The UI is connected to this exact Node via Server-Sent Events (SSE). Vert.x is holding that connection open efficiently.'
        ],
        code: `router.post("/api/orders").handler(ctx -> {
    OrderDTO order = ctx.body().asJsonObject().mapTo(OrderDTO.class);
    // Reactive non-blocking async proxy
    webClient.post(8081, "localhost", "/orders")
        .sendJson(order)
        .onSuccess(res -> ctx.response().end(res.body()))
        .onFailure(err -> ctx.fail(500));
});`
    },
    'order-service': {
        title: 'Auth & Order Service',
        icon: '🛡️',
        tech: 'Spring Boot + Apache Ignite',
        role: 'Microservice handling business invariants. Leverages Apache Ignite for distributed JSR-107 caching and partitioned session affinity.',
        ai: [
            '🤖 AI Insight: This is a real Spring Boot application validating the transaction. It acts as "The Core Logic", validating the order securely before telling the user it succeeded.',
            '💡 Deeper Look: Financial systems demand ACID strictness to prevent "dirty reads" or lost money. Spring Boot dictates this transactional boundary securely.',
            '⚙️ Architecture Note: To make this process idempotent (preventing double charges), it cross-references the transaction ID against the database instantly before proceeding.'
        ],
        code: `@PostMapping
public ResponseEntity<Order> createOrder(@RequestBody OrderDTO dto) {
    // Write-through caching with Ignite
    Order order = orderCache.put(dto.getOrderId(), map(dto));
    // Asynchronous event propagation
    kafkaTemplate.send("payments-topic", order);
    return ResponseEntity.ok(order);
}`
    },
    kafka: {
        title: 'Event Streaming Bus',
        icon: '🚀',
        tech: 'Apache Kafka',
        role: 'Distributed log-based streaming platform ensuring sub-millisecond decoupling and guaranteed event delivery.',
        ai: [
            '🤖 AI Insight: Yes, a genuine Apache Kafka broker is running live! It decouples the services. The order is sent here instantly, acting as a massive "Shock Absorber" to handle thousands of requests seamlessly.',
            '💡 Deeper Look: Without Kafka, the Order Service would be forced to wait for Analytics to finish writing. Kafka breaks this dependency, enabling "Fire and Forget" speed.',
            '⚙️ Architecture Note: In the event of a total Analytics Service failure, Kafka will safely hold up to 7 days of historical payment events on disk.'
        ],
        code: `spring:
  kafka:
    producer:
      bootstrap-servers: localhost:9092
      key-serializer: StringSerializer
      value-serializer: JsonSerializer
    topic: payments-settlement`
    },
    analytics: {
        title: 'Real-time Processor',
        icon: '⚙️',
        tech: 'Spring Cloud Stream',
        role: 'Consumes streams for real-time fraud detection, aggregation, and settlement transformation.',
        ai: [
            '🤖 AI Insight: This real-time Analytics microservice sits quietly in the background, consuming the live events from Kafka and processing them into reports without slowing down the checkout flow.',
            '💡 Deeper Look: The true power of microservices lies here. We can scale out 10 more Analytics instances horizontally if data processing falls behind, independent of the Order API.',
            '⚙️ Architecture Note: Notice how the user gets their "Success!" message *before* this node even begins its work. That is "Eventual Consistency".'
        ],
        code: `@KafkaListener(topics = "payments-topic")
public void process(OrderDTO order) {
    // Stream enrichment & Fraud Check
    Transaction tx = enrich(order);
    txHistoryRepo.save(tx);
    log.info("Settlement ready: {}", tx.getId());
}`
    },
    cassandra: {
        title: 'Merchant Ledger',
        icon: '🏦',
        tech: 'Apache Cassandra',
        role: 'High-availability NoSQL wide-column store optimized for high-velocity writes and linear scalability.',
        ai: [
            '🤖 AI Insight: A real Cassandra NoSQL database is storing these finalized transactions on the EC2! It acts as the "Big Data Ledger," optimized for writing huge amounts of historical data instantly.',
            '💡 Deeper Look: Cassandra achieves linear speed by dropping relational rules (like foreign keys) in favor of simple, continuous disk appends known as LSM-Trees.',
            '⚙️ Architecture Note: While Postgres guarantees rigid Truth, Cassandra guarantees Availability. This is the trade-off documented in the CAP theorem.'
        ],
        code: `CREATE TABLE tx_history (
    merchant_id text,
    processed_at timestamp,
    order_id uuid,
    amount decimal,
    PRIMARY KEY (merchant_id, processed_at)
) WITH CLUSTERING ORDER BY (processed_at DESC);`
    }
};

const ARCHITECTURE_DETAILS = [
    {
        id: 'gateway',
        title: 'Vert.x API Gateway (The Front Door)',
        why: 'Reactive, non-blocking I/O for 10k+ concurrent connections.',
        icon: '🚪',
        description: 'It is the very first server that your frontend talks to. It receives the checkout request and the live-streaming request. Vert.x uses an "Event Loop" (similar to Node.js, but on the JVM) to handle thousands of concurrent connections with almost zero overhead. It’s built purely for speed and routing traffic.',
        rationale: 'Traditional web servers assign one full thread to every single user request, which eats up RAM quickly if 10,000 people check out at once. Vert.x is "Reactive" and "Non-blocking," preventing server crashes during traffic spikes.',
        qa: [
            {
                q: 'What is the "Multi-Reactor" pattern in Vert.x?',
                a: 'Unlike Node.js which is single-threaded, Vert.x runs multiple event loops (one per CPU core) called "Verticles", allowing it to fully utilize modern multi-core hardware while remaining completely non-blocking.'
            },
            {
                q: 'How does it handle Server-Sent Events (SSE)?',
                a: 'Because Vert.x is asynchronous, it can hold thousands of open SSE connections to push live updates to the UI without blocking threads or running out of memory.'
            }
        ]
    },
    {
        id: 'order',
        title: 'Spring Boot (The Core Logic)',
        why: 'Enterprise-grade stability and developer productivity.',
        icon: '🛡️',
        description: 'When the API Gateway routes the payment request here, this service validates the card and creates the initial AUTHORIZED order. It immediately saves this to PostgreSQL to guarantee the transaction is recorded safely.',
        rationale: 'When dealing with money, you need absolute guarantees that a transaction is saved securely before telling the user "Success." Spring Boot is the industry standard for writing complex corporate business logic quickly and securely.',
        qa: [
            {
                q: 'Why use Spring Boot over a lighter framework?',
                a: 'Spring Boot provides a massive ecosystem for enterprise integrations (Security, Data JPA, Actuator configs), making it much faster to build complex, reliable core business services.'
            },
            {
                q: 'What is an "Idempotent" API?',
                a: 'An idempotent API ensures that if a user accidentally clicks "Pay" twice, they are only charged once. The Order Service checks the unique transaction ID before processing.'
            }
        ]
    },
    {
        id: 'kafka',
        title: 'Apache Kafka (The Shock Absorber)',
        why: 'Decoupled, durable, and scalable event streaming.',
        icon: '🏎️',
        description: 'As soon as the Order Service saves to Postgres, it yells "Hey everyone, an order was just placed!" by sending a message into Kafka. The Order Service then immediately replies to the user, without waiting to see what happens next.',
        rationale: 'Kafka decouples the application. If Black Friday hits with 50,000 orders a second, standard databases would crash trying to run analytics live. Kafka holds the messages safely in an immutable log so background services can read them at their own pace.',
        qa: [
            {
                q: 'How does Kafka guarantee message order?',
                a: 'Kafka guarantees order within a single Partition. By using a "Partition Key" (like customer_id), all related events are sent to the same partition and processed sequentially by a single consumer.'
            },
            {
                q: 'What is "Eventual Consistency"?',
                a: 'In a distributed system, eventual consistency means that while different parts of the system may not have the same data immediately, they will become consistent over time as messages are processed. This is a trade-off for high availability.'
            }
        ]
    },
    {
        id: 'ignite',
        title: 'Apache Ignite (The Speed Layer)',
        why: 'Sub-millisecond data mirroring and distributed caching.',
        icon: '⚡',
        description: 'At the exact same time the order is sent to Kafka, it is also dropped into Ignite. Ignite holds the most recent transaction states directly in RAM as a massive Global Shared Memory.',
        rationale: 'If a POS machine needs to instantly double-check "Did order #1234 go through?", querying a disk-based database like Postgres is too slow. Ignite provides sub-millisecond lookups across data centers.',
        qa: [
            {
                q: 'What is the difference between Partitioned and Replicated modes?',
                a: 'Replicated stores a full copy of the data on every node (good for small, read-heavy data). Partitioned splits the data across nodes (ideal for massive datasets and linear horizontal scaling).'
            },
            {
                q: 'Does Ignite support SQL queries?',
                a: 'Yes, Ignite provides a fully compliant SQL interface, allowing you to run complex joins and aggregations directly on the in-memory data using standard JDBC/ODBC drivers.'
            }
        ]
    },
    {
        id: 'cassandra',
        title: 'Apache Cassandra (The Big Data Ledger)',
        why: 'Linear scalability for write-heavy, immutable logs.',
        icon: '📚',
        description: 'A completely separate Spring Boot app (The Analytics Service) quietly consumes messages off of Kafka. It takes the raw orders and writes them into Cassandra to feed your frontend Reports.',
        rationale: 'Unlike PostgreSQL, which stores data in strict relational tables, Cassandra is a NoSQL database built for extreme write-velocity. It has no "Master" node; it is the perfect place to store millions of historical transactions without slowing down live payment systems.',
        qa: [
            {
                q: 'What are "Tombstones" in Cassandra?',
                a: 'Cassandra does not delete data immediately. Instead, it writes a "Tombstone" marker. During a "Compaction" process, these markers are cleaned up to reclaim storage space.'
            },
            {
                q: 'Explain the CAP Theorem and Cassandra\'s position.',
                a: 'CAP stands for Consistency, Availability, and Partition Tolerance. Cassandra is primarily an "AP" system (Availability and Partition Tolerance), allowing you to tune consistency level per query.'
            }
        ]
    },
    {
        id: 'postgres',
        title: 'PostgreSQL (The System of Record)',
        why: 'ACID-compliant relational integrity for source data.',
        icon: '🏦',
        description: 'PostgreSQL provides full support for ACID (Atomicity, Consistency, Isolation, Durability) transactions and is used as the initial "source of truth" for the Order validation process.',
        rationale: 'While NoSQL is great for logs, financial balances and merchant contracts require strict relational integrity. Postgres ensures that an order and its payment status are perfectly linked and consistently stored before moving down the pipeline.',
        qa: [
            {
                q: 'What is MVCC (Multi-Version Concurrency Control)?',
                a: 'MVCC allows multiple users to read and write to the same data simultaneously without locking. Each transaction sees a snapshot of the data, preventing "Dirty Reads" and improving performance.'
            },
            {
                q: 'Why use a Relational DB over a NoSQL DB for Orders?',
                a: 'Orders have complex relationships and require strict schema enforcement. Relational databases provide foreign keys and complex joins that are essential for accurate financial accounting.'
            }
        ]
    }
];

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const initialState = {
    events: [],
    activeNodes: [],
    selectedNode: null,
    aiIndex: 0,
    metrics: {
        gateway: { requests: 0, latency: 0 },
        'order-service': { requests: 0, latency: 0 },
        kafka: { requests: 0, latency: 0 },
        analytics: { requests: 0, latency: 0 },
        cassandra: { requests: 0, latency: 0 }
    },
    statements: []
};

function demoReducer(state, action) {
    switch (action.type) {
        case 'ADD_EVENT':
            return { ...state, events: [action.payload, ...state.events].slice(0, 50) };
        case 'SET_ACTIVE_NODES':
            return { ...state, activeNodes: action.payload };
        case 'SET_SELECTED_NODE':
            return { ...state, selectedNode: action.payload, aiIndex: 0 };
        case 'NEXT_AI_INSIGHT':
            return {
                ...state,
                aiIndex: (state.aiIndex + 1) % (NODE_DETAILS[state.selectedNode]?.ai?.length || 1)
            };
        case 'UPDATE_METRICS':
            return {
                ...state,
                metrics: {
                    ...state.metrics,
                    [action.node]: {
                        requests: state.metrics[action.node].requests + 1,
                        latency: action.latency
                    }
                }
            };
        case 'SET_STATEMENTS':
            return { ...state, statements: action.payload };
        case 'CLEAR_DATA':
            return {
                ...state,
                events: [],
                statements: [],
                metrics: initialState.metrics
            };
        default:
            return state;
    }
}

const BackendDemo = () => {
    const [state, dispatch] = useReducer(demoReducer, initialState);
    const [posData, setPosData] = useState({
        cardNumber: '4532 **** **** 8812',
        amount: '1250.00',
        merchantId: '1234567890',
        merchantName: 'Walmart Mexico Central',
        cmNumber: '123456789012345',
        cmName: 'Juan Perez',
        currency: 'MXN'
    });
    const [focusedField, setFocusedField] = useState('amount');
    const [isLoading, setIsLoading] = useState(false);
    const [packets, setPackets] = useState([]);
    const [showGuide, setShowGuide] = useState(false);

    const handleKeypadPress = (val) => {
        if (val === '*' || val === '#') return;
        setPosData(prev => {
            const currentVal = prev[focusedField] || '';
            return { ...prev, [focusedField]: currentVal + val };
        });
    };

    const clearField = () => {
        setPosData(prev => ({ ...prev, [focusedField]: '' }));
    };

    const getCMReport = () => {
        const report = (state.statements || []).reduce((acc, tx) => {
            const id = tx.customerId || tx.customer_id || 'UNKNOWN';
            const name = tx.customerName || tx.customer_name || 'N/A';
            const key = `${id}|${name}`;
            const amount = parseFloat(tx.amount) || 0;
            acc[key] = (acc[key] || 0) + amount;
            return acc;
        }, {});
        return Object.entries(report)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, total]) => {
                const [id, name] = key.split('|');
                return { id, name, total };
            });
    };

    const getMerchantReport = () => {
        const report = (state.statements || []).reduce((acc, tx) => {
            const id = tx.merchantId || tx.merchant_id || 'UNKNOWN';
            const name = tx.merchantName || tx.merchant_name || 'N/A';
            const key = `${id}|${name}`;
            const amount = parseFloat(tx.amount) || 0;
            acc[key] = (acc[key] || 0) + amount;
            return acc;
        }, {});
        return Object.entries(report)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, total]) => {
                const [id, name] = key.split('|');
                return { id, name, total };
            });
    };

    const cmReport = getCMReport();
    const merchantReport = getMerchantReport();
    const totalCMSpeak = cmReport.reduce((acc, row) => acc + row.total, 0);
    const totalMerchantSales = merchantReport.reduce((acc, row) => acc + row.total, 0);
    const totalTxs = state.statements.length;
    const isBalanced = totalTxs > 0 && Math.abs(totalCMSpeak - totalMerchantSales) < 0.01;

    const fetchStatements = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/payments/statements?t=${Date.now()}`, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            if (response.ok) {
                const data = await response.json();
                // Sort globally descending by processedAt since Cassandra partition keys scramble the global order
                const sortedData = data.sort((a, b) => new Date(b.processedAt || b.processed_at) - new Date(a.processedAt || a.processed_at));
                dispatch({ type: 'SET_STATEMENTS', payload: sortedData });
            }
        } catch (err) {
            console.error('Failed to fetch statements', err);
        }
    }, []);

    const purgeLedger = async () => {
        if (!window.confirm('Are you sure you want to purge all transaction records from Cassandra?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/payments/statements`, {
                method: 'DELETE'
            });
            if (response.ok) {
                dispatch({ type: 'CLEAR_DATA' });
                addLog('success', 'Cassandra Cluster: History purged successfully.');
            }
        } catch (err) {
            addLog('error', `Purge Failed: ${err.message}`);
        }
    };

    const addLog = useCallback((type, message, node = 'pos') => {
        dispatch({
            type: 'ADD_EVENT',
            payload: {
                id: Date.now() + Math.random(),
                time: new Date().toLocaleTimeString(),
                type,
                message,
                node
            }
        });
    }, []);

    useEffect(() => {
        fetchStatements();
        const eventSource = new EventSource(`${API_BASE_URL}/api/events`);

        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'connected') return;

            const jsonSnippet = JSON.stringify({ id: data.orderId?.substring(0, 8), status: data.status, amount: data.amount });
            addLog('order', `Event Loop: Received Broadcast ${jsonSnippet}`, data.node || 'gateway');
            animatePipeline(data);
            setTimeout(fetchStatements, 3000);
        };

        return () => eventSource.close();
    }, [addLog, fetchStatements]);

    const animatePipeline = async (actualData = null) => {
        const sequence = ['gateway', 'order-service', 'kafka', 'analytics', 'cassandra'];
        const payload = actualData ?
            JSON.stringify({ id: actualData.orderId?.substring(0, 8), amt: actualData.amount, mkt: actualData.merchantId }) :
            JSON.stringify({ amount: posData.amount, status: 'PENDING' });

        for (let i = 0; i < sequence.length; i++) {
            const node = sequence[i];
            dispatch({ type: 'SET_ACTIVE_NODES', payload: sequence.slice(0, i + 1) });
            dispatch({ type: 'UPDATE_METRICS', node, latency: Math.floor(Math.random() * 20) + 5 });

            if (i < sequence.length - 1) {
                const pId = Date.now() + Math.random();
                addLog('info', `[IO-Thread] Forwarding Payload: ${payload}`, node);
                setPackets(p => [...p, { id: pId, from: node, data: i === 0 ? `$${posData.amount}` : 'JSON' }]);
                setTimeout(() => setPackets(p => p.filter(x => x.id !== pId)), 800);
            }
            await new Promise(r => setTimeout(r, 800));
        }
        setTimeout(() => dispatch({ type: 'SET_ACTIVE_NODES', payload: [] }), 2000);
    };

    const processPayment = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        addLog('info', `POS: Encrypting payload for ${posData.merchantId}...`);

        try {
            const response = await fetch(`${API_BASE_URL}/api/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerId: posData.cmNumber,
                    customerName: posData.cmName,
                    amount: posData.amount,
                    cardNumber: posData.cardNumber,
                    merchantId: posData.merchantId,
                    merchantName: posData.merchantName,
                    currency: posData.currency
                })
            });

            if (response.ok) {
                addLog('success', 'POS: Auth Request Handled by Gateway.', 'gateway');
                animatePipeline();
            }
        } catch (err) {
            addLog('error', `Network Error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="backend-demo-container payment-demo">
            <div className="demo-header">
                <button className="back-home-btn" onClick={() => window.location.hash = ''}>
                    ← Back to Resume
                </button>
                <div className="header-badge">Live Microservices Architecture</div>
                <h1>Mission-Critical Payment Pipeline</h1>
                <p>Visualizing Real-time Settlement for Walmart Mexico Tier-1 Infrastructure</p>
            </div>

            {/* Expandable Architecture Knowledge Base */}
            <div className={`architecture-guide-wrapper glass ${showGuide ? 'expanded' : ''}`}>
                <div className="guide-trigger" onClick={() => setShowGuide(!showGuide)}>
                    <div className="trigger-title">
                        <span role="img" aria-label="book">📚</span> Technical Architecture Knowledge Base
                    </div>
                    <div className="trigger-action">{showGuide ? 'Collapse Knowledge Base △' : 'Open Knowledge Base ▽'}</div>
                </div>

                {showGuide && (
                    <div className="guide-content">
                        <div className="guide-intro">
                            <p>This technical knowledge base provides an in-depth look at our <strong>High Partition Tolerance</strong> and <strong>Low Latency</strong> pipeline. Each component choice represents a critical decision in distributed system design.</p>
                        </div>
                        <div className="guide-grid">
                            {ARCHITECTURE_DETAILS.map(item => (
                                <div key={item.id} className="guide-item high-density">
                                    <div className="item-header">
                                        <span className="item-icon">{item.icon}</span>
                                        <h4>{item.title}</h4>
                                    </div>
                                    <div className="item-body">
                                        <div className="deep-dive-section">
                                            <p className="description-text">{item.description}</p>
                                        </div>

                                        <div className="insight-row">
                                            <span className="insight-label">ARCHITECTURAL RATIONALE:</span>
                                            <span className="insight-text">{item.rationale}</span>
                                        </div>

                                        <div className="qa-section">
                                            <div className="qa-header">EXPERT Q&A</div>
                                            {item.qa.map((pair, idx) => (
                                                <div key={idx} className="qa-pair">
                                                    <div className="question">Q: {pair.q}</div>
                                                    <div className="answer">A: {pair.a}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="guide-footer">
                            <blockquote>
                                "Advanced System Design Tip: In high-velocity payment systems, we often trade <strong>Immediate Consistency</strong> for <strong>High Availability</strong> to ensure that the POS terminal never stalls due to network partitioned nodes."
                            </blockquote>
                        </div>
                    </div>
                )}
            </div>

            <div className="demo-main-grid">
                {/* Left: Interactive POS */}
                <div className="pos-card glass">
                    <div className="pos-device-frame">
                        <div className="pos-receipt-slot"></div>
                        <div className="pos-screen">
                            <div className="pos-screen-header">
                                <span className="network-indicator">● LIVE 5G</span>
                                <span className="battery-indicator">98% 🔋</span>
                            </div>
                            <h3 className="pos-machine-title">TERMINAL ONLINE</h3>

                            <form onSubmit={processPayment} className="pos-form">
                                <div className="pos-field-row">
                                    <div className="input-group">
                                        <label>MERCHANT ID</label>
                                        <input
                                            type="text"
                                            maxLength="10"
                                            value={posData.merchantId}
                                            onFocus={() => setFocusedField('merchantId')}
                                            onChange={e => setPosData({ ...posData, merchantId: e.target.value.replace(/\D/g, '') })}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>MERCHANT NAME</label>
                                        <input
                                            type="text"
                                            placeholder="Merchant Name"
                                            value={posData.merchantName}
                                            onFocus={() => setFocusedField('merchantName')}
                                            onChange={e => setPosData({ ...posData, merchantName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="pos-field-row">
                                    <div className="input-group">
                                        <label>CARD MEMBER #</label>
                                        <input
                                            type="text"
                                            maxLength="15"
                                            value={posData.cmNumber}
                                            onFocus={() => setFocusedField('cmNumber')}
                                            onChange={e => setPosData({ ...posData, cmNumber: e.target.value.replace(/\D/g, '') })}
                                        />
                                    </div>
                                    <div className="input-group">
                                        <label>CARD MEMBER NAME</label>
                                        <input
                                            type="text"
                                            placeholder="Customer Name"
                                            value={posData.cmName}
                                            onFocus={() => setFocusedField('cmName')}
                                            onChange={e => setPosData({ ...posData, cmName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="pos-field-row">
                                    <div className="input-group">
                                        <label>AMOUNT</label>
                                        <div className="amount-input-wrapper">
                                            <span className="currency-symbol">$</span>
                                            <input
                                                type="text"
                                                className="amount-input"
                                                value={posData.amount}
                                                onFocus={() => setFocusedField('amount')}
                                                onChange={e => setPosData({ ...posData, amount: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="input-group">
                                        <label>CURRENCY</label>
                                        <select value={posData.currency} onChange={e => setPosData({ ...posData, currency: e.target.value })}>
                                            <option>MXN</option>
                                            <option>USD</option>
                                            <option>EUR</option>
                                        </select>
                                    </div>
                                </div>

                                <button type="submit" className="pos-authorize-btn" disabled={isLoading}>
                                    {isLoading ? 'AUTHORIZING...' : 'AUTHORIZE TRANSACTION'}
                                </button>
                            </form>
                        </div>

                        <div className="pos-keypad">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'CLR', 0, 'SUB'].map((key) => (
                                <div
                                    key={key}
                                    className={`keypad-btn ${key === 'CLR' ? 'btn-clr' : ''} ${key === 'SUB' ? 'btn-sub' : ''}`}
                                    onClick={() => {
                                        if (key === 'CLR') clearField();
                                        else if (key === 'SUB') document.querySelector('.pos-form').requestSubmit();
                                        else handleKeypadPress(key);
                                    }}
                                >
                                    <span className="key-val">{key}</span>
                                    {typeof key === 'number' && key > 0 && <span className="key-sub">{['', 'ABC', 'DEF', 'GHI', 'JKL', 'MNO', 'PQRS', 'TUV', 'WXYZ'][key - 1]}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: Architectural Flow */}
                <div className="pipeline-wrapper glass">
                    <div className="pipeline-viz">
                        <Node
                            id="gateway"
                            {...NODE_DETAILS.gateway}
                            active={state.activeNodes.includes('gateway')}
                            selected={state.selectedNode === 'gateway'}
                            onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: 'gateway' })}
                        />
                        <Connector hasPacket={packets.find(p => p.from === 'gateway')} />

                        <Node
                            id="order-service"
                            {...NODE_DETAILS['order-service']}
                            active={state.activeNodes.includes('order-service')}
                            selected={state.selectedNode === 'order-service'}
                            onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: 'order-service' })}
                        />
                        <Connector hasPacket={packets.find(p => p.from === 'order-service')} />

                        <Node
                            id="kafka"
                            {...NODE_DETAILS.kafka}
                            active={state.activeNodes.includes('kafka')}
                            selected={state.selectedNode === 'kafka'}
                            onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: 'kafka' })}
                        />
                        <Connector hasPacket={packets.find(p => p.from === 'kafka')} />

                        <Node
                            id="analytics"
                            {...NODE_DETAILS.analytics}
                            active={state.activeNodes.includes('analytics')}
                            selected={state.selectedNode === 'analytics'}
                            onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: 'analytics' })}
                        />
                        <Connector hasPacket={packets.find(p => p.from === 'analytics')} />

                        <Node
                            id="cassandra"
                            {...NODE_DETAILS.cassandra}
                            active={state.activeNodes.includes('cassandra')}
                            selected={state.selectedNode === 'cassandra'}
                            onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: 'cassandra' })}
                        />
                    </div>

                    {/* Dynamic Inspector */}
                    {state.selectedNode ? (
                        <div className="inspector-panel animate-in">
                            <div className="inspector-header">
                                <div className="inspector-title">
                                    <span className="tech-badge">{NODE_DETAILS[state.selectedNode].tech}</span>
                                    <h4>{NODE_DETAILS[state.selectedNode].title}</h4>
                                </div>
                                <button className="close-btn" onClick={() => dispatch({ type: 'SET_SELECTED_NODE', payload: null })}>✕</button>
                            </div>
                            <p className="inspector-role">{NODE_DETAILS[state.selectedNode].role}</p>
                            {NODE_DETAILS[state.selectedNode].ai && (
                                <div className="inspector-ai-insight">
                                    <div className="ai-content">
                                        {NODE_DETAILS[state.selectedNode].ai[state.aiIndex]}
                                    </div>
                                    <button
                                        className="ai-insight-btn primary-action glossy"
                                        onClick={() => dispatch({ type: 'NEXT_AI_INSIGHT' })}
                                    >
                                        <span role="img" aria-label="sparkles">✨</span> Ask AI for deeper insight
                                    </button>
                                </div>
                            )}
                            <div className="inspector-content-split">
                                <div className="inspector-logs">
                                    <div className="section-header">Live Service Logs</div>
                                    <div className="mini-logs">
                                        {state.events.filter(e => e.node === state.selectedNode).map(log => (
                                            <div key={log.id} className={`mini-log-entry ${log.type}`}>
                                                <span className="log-time">{log.time}</span>
                                                <span className="log-msg">{log.message}</span>
                                            </div>
                                        ))}
                                        {state.events.filter(e => e.node === state.selectedNode).length === 0 && (
                                            <div className="empty-logs">Listening for incoming node events...</div>
                                        )}
                                    </div>
                                </div>
                                <div className="code-block">
                                    <div className="section-header">Implementation Logic</div>
                                    <pre><code>{NODE_DETAILS[state.selectedNode].code}</code></pre>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="inspector-placeholder">
                            <span role="img" aria-label="info">ℹ️</span> Click a service node to inspect its architectural role and implementation logic
                        </div>
                    )}
                </div>
            </div>

            {/* Holistic Architecture Diagram Section */}
            <div className="architecture-diagram glass">
                <div className="diagram-header">
                    <h3><span role="img" aria-label="diagram">🎨</span> System Architecture & Flow Control</h3>
                    <p>End-to-end topological map of the payment ecosystem</p>
                </div>
                <div className="diagram-content">
                    <div className="flow-step">
                        <div className="diagram-item-icon">{NODE_DETAILS.gateway.icon}</div>
                        <div className="step-point">1</div>
                        <div className="step-info">
                            <h5>Client Ingress</h5>
                            <p>Reactive POS payloads entering Edge Gateway via REST/SSE.</p>
                        </div>
                    </div>
                    <div className="flow-arrow">→</div>
                    <div className="flow-step">
                        <div className="diagram-item-icon">{NODE_DETAILS['order-service'].icon}</div>
                        <div className="step-point">2</div>
                        <div className="step-info">
                            <h5>Distributed State</h5>
                            <p>Ignite-backed persistence & session mirroring across nodes.</p>
                        </div>
                    </div>
                    <div className="flow-arrow">→</div>
                    <div className="flow-step">
                        <div className="diagram-item-icon">{NODE_DETAILS.kafka.icon}</div>
                        <div className="step-point">3</div>
                        <div className="step-info">
                            <h5>Event Mesh</h5>
                            <p>Asynchronous decoupling via managed Kafka stream topics.</p>
                        </div>
                    </div>
                    <div className="flow-arrow">→</div>
                    <div className="flow-step">
                        <div className="diagram-item-icon">{NODE_DETAILS.cassandra.icon}</div>
                        <div className="step-point">4</div>
                        <div className="step-info">
                            <h5>Analytics Sink</h5>
                            <p>Cassandra Cluster writing wide-column ledger at linear scale.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom: Real-time Reports */}
            <div className="reports-grid">
                <div className="report-card glass">
                    <div className="report-header">
                        <h3><span role="img" aria-label="cm-report">👤</span> Card Member Report</h3>
                    </div>
                    <div className="report-table-container">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>CM NUMBER</th>
                                    <th>NAME</th>
                                    <th>TOTAL SPEND (MXN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cmReport.map(row => (
                                    <tr key={row.id}>
                                        <td><code>{row.id}</code></td>
                                        <td>{row.name}</td>
                                        <td className="amount-col">${row.total.toFixed(2)}</td>
                                    </tr>
                                ))}
                                {state.statements.length === 0 && <tr><td colSpan="3" className="empty-state">No data</td></tr>}
                            </tbody>
                            {cmReport.length > 0 && (
                                <tfoot className="report-footer">
                                    <tr>
                                        <td colSpan="2">TOTAL CM SPEND</td>
                                        <td className="amount-col total-val">${totalCMSpeak.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                <div className="report-card glass">
                    <div className="report-header">
                        <h3><span role="img" aria-label="merch-report">🏬</span> Merchant Report</h3>
                    </div>
                    <div className="report-table-container">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>MERCHANT ID</th>
                                    <th>NAME</th>
                                    <th>TOTAL SALES (MXN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {merchantReport.map(row => (
                                    <tr key={row.id}>
                                        <td><code>{row.id}</code></td>
                                        <td>{row.name}</td>
                                        <td className="amount-col">${row.total.toFixed(2)}</td>
                                    </tr>
                                ))}
                                {state.statements.length === 0 && <tr><td colSpan="3" className="empty-state">No data</td></tr>}
                            </tbody>
                            {merchantReport.length > 0 && (
                                <tfoot className="report-footer">
                                    <tr>
                                        <td colSpan="2">TOTAL MERCHANT SALES</td>
                                        <td className="amount-col total-val">${totalMerchantSales.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {/* Middle: Control & Balance Dashboard */}
            <div className="control-dashboard glass">
                <div className="dashboard-header">
                    <h3><span role="img" aria-label="balance">⚖️</span> Balance & Control Monitoring</h3>
                    <div className={`balance-status ${isBalanced ? 'status-balanced' : 'status-pending'}`}>
                        {isBalanced ? '✅ SYSTEM IN BALANCE' : '⌛ RECONCILIATION PENDING'}
                    </div>
                </div>
                <div className="dashboard-grid">
                    <div className="metric-box">
                        <label>Ledger Integrity</label>
                        <div className="metric-val">{totalTxs} Records</div>
                    </div>
                    <div className="metric-box">
                        <label>Aggregated CM Spend</label>
                        <div className="metric-val text-yellow">${totalCMSpeak.toFixed(2)}</div>
                    </div>
                    <div className="metric-box">
                        <label>Aggregated Merchant Sales</label>
                        <div className="metric-val text-yellow">${totalMerchantSales.toFixed(2)}</div>
                    </div>
                    <div className="metric-box">
                        <label>Control Variance</label>
                        <div className={`metric-val ${isBalanced ? 'text-green' : 'text-red'}`}>
                            ${Math.abs(totalCMSpeak - totalMerchantSales).toFixed(2)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom: Real-time Ledger */}
            <div className="ledger-card glass">
                <div className="ledger-header">
                    <h3><span role="img" aria-label="ledger">🏦</span> Distributed Transaction Ledger (Cassandra)</h3>
                    <div className="ledger-controls">
                        <span className="live-indicator">LIVE</span>
                        <button className="refresh-btn" onClick={fetchStatements} title="Refresh Ledger">↻</button>
                        <button className="purge-btn" onClick={purgeLedger} title="Purge Ledger">🗑️</button>
                    </div>
                </div>
                <div className="ledger-table-container">
                    <table className="ledger-table">
                        <thead>
                            <tr>
                                <th>TX_ID</th>
                                <th>MERCHANT</th>
                                <th>AMOUNT (MXN)</th>
                                <th>STATUS</th>
                                <th>PERSISTED_AT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.statements.map(tx => (
                                <tr key={tx.orderId}>
                                    <td><code>{tx.orderId.substring(0, 8)}</code></td>
                                    <td>{tx.merchantId}</td>
                                    <td className="amount-col">${parseFloat(tx.amount).toFixed(2)}</td>
                                    <td><span className={`status-pill ${tx.status.toLowerCase()}`}>{tx.status}</span></td>
                                    <td>{new Date(tx.processedAt).toLocaleTimeString()}</td>
                                </tr>
                            ))}
                            {state.statements.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="empty-state">No transaction records in Cassandra cluster</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const Node = ({ id, icon, title, active, selected, onClick }) => (
    <div className={`service-node ${active ? 'active' : ''} ${selected ? 'selected' : ''}`} onClick={onClick}>
        <div className="node-icon">{icon}</div>
        <div className="node-label">
            <span className="node-id">{id}</span>
            <span className="node-name">{title.split(' ')[0]}</span>
        </div>
        {active && <div className="node-pulse"></div>}
    </div>
);

const Connector = ({ hasPacket }) => (
    <div className="pipeline-connector">
        <div className="connector-line"></div>
        {hasPacket && (
            <div className="data-packet">
                <div className="packet-dot"></div>
                <div className="packet-label">{hasPacket.data}</div>
            </div>
        )}
    </div>
);

export default BackendDemo;
